import { ApplicantNameCell } from './ApplicantNameCell';
import { ViewMorePopover } from '@/components/ats/ViewMorePopover/ViewMorePopover';
import { useDateFormat } from '@/hooks';
import { secondsToHumanTime } from '@/pages/reports/non-ats-reports/ats/table';
import { getInterviewTime } from '@/utils/interview-utils';
import { KTag, KText } from '@/ui-library/components';
import { Flex, Link } from '@chakra-ui/react';

export const NameCell = (row) => {
  const atsApplication = row?.original?.ats_application;
  const { ats_job = {} } = row?.original?.job ?? {};
  const { id: jobId } = ats_job ?? {};

  return <ApplicantNameCell atsApplication={atsApplication} jobId={jobId} />;
};

export const SourcedByName = (row) => {
  const { action_user_name = '' } = row?.original ?? {};

  return (
    <Flex w="100%">
      <KText maxW="100%" enableTooltip={true} isTruncated size="sm-regular">
        {action_user_name || '-'}
      </KText>
    </Flex>
  );
};

export const JobName = (row) => {
  const { ats_job = {} } = row?.original?.job ?? {};
  const { name, id } = ats_job || {};
  return (
    <Link href={`/jobs/${id}`} target="_blank" w="100%">
      <KText
        maxW="100%"
        enableTooltip={true}
        isTruncated
        size="sm-regular"
        _hover={{
          textDecoration: 'underline',
          cursor: 'pointer',
          color: 'primary-500',
        }}
      >
        {name || '-'}
      </KText>
    </Link>
  );
};

export const StartingStage = (row) => {
  const { starting_stage } = row?.original ?? {};
  return (
    <>
      {starting_stage ? (
        <KTag
          maxW="100%"
          enableTooltip={true}
          isTruncated
          size="sm-regular"
          value={starting_stage || '-'}
        />
      ) : (
        '-'
      )}
    </>
  );
};

export const DestinationStage = (row) => {
  const { destination_stage } = row?.original ?? {};
  return (
    <>
      {destination_stage ? (
        <KTag
          maxW="100%"
          enableTooltip={true}
          isTruncated
          size="sm-regular"
          value={destination_stage || '-'}
        />
      ) : (
        '-'
      )}
    </>
  );
};

export const ScreenedBy = (row) => {
  const { action_user_name = '' } = row?.original ?? {};

  return (
    <Flex w="100%">
      <KText maxW="100%" enableTooltip={true} isTruncated size="sm-regular">
        {action_user_name || '-'}
      </KText>
    </Flex>
  );
};

export const InterviewName = (row) => {
  const { name, id } = row?.original?.interview?.ats_interview ?? {};
  return (
    <Link href={`/interviews/${id}`} target="_blank" w="100%">
      <KText
        maxW="100%"
        enableTooltip={true}
        isTruncated
        size="sm-regular"
        _hover={{
          textDecoration: 'underline',
          color: 'primary-500',
          cursor: 'pointer',
        }}
      >
        {name || '-'}
      </KText>
    </Link>
  );
};

export const InterviewStatus = (row) => {
  const { interview_status = '' } = row?.original ?? {};
  const labelMap = {
    ended: {
      label: 'Interview ended',
      color: 'pastel-2-dark',
      bg: 'pastel-2-light',
      borderColor: 'transparent',
    },
    cancelled: {
      label: 'Interview canceled',
      color: 'pastel-1-dark',
      bg: 'pastel-1-light',
      borderColor: 'transparent',
    },
    not_started: {
      label: 'Interview scheduled',
      color: 'pastel-1-dark',
      bg: 'pastel-2-light',
      borderColor: 'transparent',
    },
    in_progress: {
      label: 'Interview in-progress',
      color: 'pastel-6-dark',
      bg: 'pastel-6-light',
      borderColor: 'transparent',
    },
  };
  const { label, color, bg } = labelMap[interview_status] || {};
  return <KTag bg={bg} color={color} value={label || '-'} />;
};

export const Interviewers = (row) => {
  const { interviewers = '' } = row?.original ?? {};

  return (
    <Flex w="100%" alignItems="center" gap="6px">
      <KText maxW="100%" enableTooltip={true} isTruncated size="sm-regular">
        {interviewers || '-'}
      </KText>
    </Flex>
  );
};

export const InterviewDateTime = (row) => {
  const { start_time = '', end_time = '' } =
    row?.original?.interview?.ats_interview ?? {};
  const { dateFormat, timeFormat, currUserTimezone } = useDateFormat();
  const interviewTimeResult = getInterviewTime({
    startTime: start_time,
    endTime: end_time,
    timeFormat: timeFormat,
    dateFormat: dateFormat.label,
    currUserTimezone: currUserTimezone,
  });
  const { dateLabel, timeLabel } =
    typeof interviewTimeResult === 'string'
      ? { dateLabel: '-', timeLabel: '-' }
      : interviewTimeResult;

  return (
    <>
      {start_time && end_time ? (
        <KText maxW="100%" enableTooltip={true} isTruncated size="sm-regular">
          {dateLabel} {timeLabel}
        </KText>
      ) : (
        '-'
      )}
    </>
  );
};

export const StageName = (row) => {
  const { stage_name = '' } = row?.original ?? {};
  return (
    <>
      {stage_name ? (
        <KTag
          maxW="100%"
          enableTooltip={true}
          isTruncated
          size="sm-regular"
          value={stage_name || '-'}
        />
      ) : (
        '-'
      )}
    </>
  );
};

export const ScheduledBy = (row) => {
  const { action_user_name = '' } = row?.original ?? {};

  return (
    <Flex w="100%">
      <KText maxW="100%" enableTooltip={true} isTruncated size="sm-regular">
        {action_user_name || '-'}
      </KText>
    </Flex>
  );
};

export const RescheduledBy = (row) => {
  const { action_user_name = '' } = row?.original ?? {};

  return (
    <Flex w="100%">
      <KText maxW="100%" enableTooltip={true} isTruncated size="sm-regular">
        {action_user_name || '-'}
      </KText>
    </Flex>
  );
};

export const OfferAcceptanceStatus = (row) => {
  const { offer_status = '' } = row?.original ?? {};
  const labelMap = {
    Sent: {
      label: 'Sent',
      borderColor: 'transparent',
    },
    Accepted: {
      label: 'Accepted',
      color: 'pastel-2-dark',
      bg: 'pastel-2-light',
      borderColor: 'transparent',
    },
    Pending: {
      label: 'Pending',
      color: '#D98647',
      bg: '#FFF0E0',
      borderColor: 'transparent',
    },
    Rejected: {
      label: 'Rejected',
      color: 'pastel-1-dark',
      bg: 'pastel-1-light',
      borderColor: 'transparent',
    },
  };
  const { label, color, bg } = labelMap[offer_status] || {};
  return <KTag bg={bg} color={color} value={label || '-'} />;
};

export const TimeToOffer = (row) => {
  const { time_to_offer } = row?.original ?? {};
  return (
    <KText maxW="100%" enableTooltip={true} isTruncated size="sm-regular">
      {secondsToHumanTime(time_to_offer)}
    </KText>
  );
};

export const OfferRevision = (row) => {
  const { offer_revisions_count } = row?.original ?? {};
  return (
    <KText maxW="100%" enableTooltip={true} isTruncated size="sm-regular">
      {offer_revisions_count || '-'}
    </KText>
  );
};

export const TimeToHire = (row) => {
  const { time_to_hire } = row?.original ?? {};
  return (
    <KText maxW="100%" enableTooltip={true} isTruncated size="sm-regular">
      {secondsToHumanTime(time_to_hire)}
    </KText>
  );
};

export const Source = (row) => {
  const { ats_candidate_source = {} } = row?.original?.source ?? {};
  const { name } = ats_candidate_source ?? {};

  return (
    <Flex w="100%">
      <KText maxW="100%" enableTooltip={true} isTruncated size="sm-regular">
        {name || '-'}
      </KText>
    </Flex>
  );
};

export const RejectionType = (row) => {
  const { rejection_type } = row?.original ?? {};
  return (
    <KText maxW="100%" enableTooltip={true} isTruncated size="sm-regular">
      {rejection_type || '-'}
    </KText>
  );
};

export const RejectionReason = (row) => {
  const { rejection_reason } = row?.original ?? {};
  return (
    <KText maxW="100%" enableTooltip={true} isTruncated size="sm-regular">
      {rejection_reason || '-'}
    </KText>
  );
};

const interviewTypeValueMap = {
  one_on_one: '1 on 1',
  panel: 'Panel',
};

const interviewLocationValueMap = {
  zoom: 'Zoom',
  google_meet: 'Google Meet',
  phone_interview: 'Phone Interview',
  onsite: 'Onsite',
  teams: 'Teams',
  hackerrank: 'HackerRank',
};

export const InterviewType = (row) => {
  const { interview_type = '' } = row?.original ?? {};
  const label = interviewTypeValueMap[interview_type] ?? interview_type;
  return (
    <KText maxW="100%" enableTooltip={true} isTruncated size="sm-regular">
      {label || '-'}
    </KText>
  );
};

export const InterviewDuration = (row) => {
  const duration = row?.original?.interview_duration || 0;
  return (
    <KText maxW="100%" enableTooltip={true} isTruncated size="sm-regular">
      {secondsToHumanTime(duration)}
    </KText>
  );
};

export const InterviewLocation = (row) => {
  const { interview_location_type = '' } = row?.original ?? {};
  const label =
    interviewLocationValueMap[interview_location_type] ??
    interview_location_type;
  return (
    <KText maxW="100%" enableTooltip={true} isTruncated size="sm-regular">
      {label || '-'}
    </KText>
  );
};

export const DepartmentName = (row) => {
  const { name = '' } = row?.original?.department?.ats_department ?? {};
  return (
    <KText maxW="100%" enableTooltip={true} isTruncated size="sm-regular">
      {name || '-'}
    </KText>
  );
};

const parseOfficeNames = (value: unknown): string[] => {
  if (value === null || value === undefined) return [];
  if (Array.isArray(value)) return value.map(String);
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch {
      return value.trim() ? [value.trim()] : [];
    }
  }
  return [];
};

const renderOfficeInitial = (items: string[]) => (
  <Flex w="100%" gap="6px" alignItems="center">
    {items?.map((name, i) => (
      <KText
        key={i}
        maxW="130px"
        enableTooltip={true}
        isTruncated
        size="sm-regular"
      >
        {name}
      </KText>
    ))}
  </Flex>
);

const renderOfficeOthers = (list: string[]) => (
  <Flex flexDir="column" gap=".25em">
    {list?.map((name, i) => (
      <Flex key={i} alignItems="center" gap=".25em">
        <KText
          fontSize="sm"
          color="secondary-700"
          isTruncated
          enableTooltip={true}
        >
          {name}
        </KText>
      </Flex>
    ))}
  </Flex>
);

export const OfficeNames = (row) => {
  const { office_names } = row?.original ?? {};
  const offices = parseOfficeNames(office_names);

  return (
    <Flex w="100%">
      <ViewMorePopover
        list={offices}
        max={1}
        type="officeList"
        renderInitial={renderOfficeInitial}
        renderOthers={renderOfficeOthers}
        emptyMsg="-"
        trigger="hover"
      />
    </Flex>
  );
};

export const CancelledBy = (row) => {
  const { action_user_name = '' } = row?.original ?? {};
  return (
    <Flex w="100%">
      <KText maxW="100%" enableTooltip={true} isTruncated size="sm-regular">
        {action_user_name || '-'}
      </KText>
    </Flex>
  );
};
